// AI-CONTEXT: See docs/patterns.md#hook-permission-gate

import { useRef, useState } from "react"
import usePermissions from "../../hooks/usePermissions"
import permissionLabel from "../../helpers/permissionLabel"
import deniedControl from "../../helpers/deniedControl"
import Modal from "./Modal"

/**
 * Hoe breed de rechterrand van een scrollend tekstvak als schuifbalk telt.
 * Op de Mac zweeft de schuifbalk óver de tekst en neemt hij geen breedte in
 * (in Chrome nagemeten: een druk erop gaf x 293 bij een inhoudsbreedte van
 * 298), dus `offsetX > clientWidth` alleen mist hem.
 */
const SCROLLBAR_ZONE = 16

/**
 * Of een druk op de schuifbalk van een veld viel — wie die pakt wil een lang
 * verslag lezen, niet bewerken. Alleen bij een veld dat werkelijk scrolt;
 * anders is de rechterrand gewoon het veld.
 *
 * @param {Element} control
 * @param {number} offsetX - Positie van de druk binnen het veld
 * @returns {boolean}
 */
const onScrollbar = (control, offsetX) =>
    control.scrollHeight > control.clientHeight
    && offsetX > control.clientWidth - SCROLLBAR_ZONE

// Wraps a page's editable content in a <fieldset> that is disabled when the
// user lacks the area's `.edit` permission. The browser natively disables
// every nested <input>, <textarea>, <select> and <button> — no per-control
// changes needed. Permissive while permissions load.
//
// `children` may be a render function `(canEdit) => jsx` — use that to also
// hide controls a disabled fieldset does NOT cover: icon-only click handlers
// (<img onClick>, MUI icons), TinyMCE editors, and Table `addRow`.
//
// Een uitgeschakeld veld zegt uit zichzelf niets. Elk invoervak staat er nog,
// de knoppen staan er nog, en er gebeurt niets — dat is voor de lezer niet te
// onderscheiden van kapot, en het werd ook als kapot gemeld (docs/inbox.md
// Bug #240). Daarom hoort er een melding bij die drie dingen zegt: dat je
// alleen kunt meekijken, welk recht daarvoor nodig is, en bij wie je het
// haalt. Het recht wordt genoemd met het label uit de catalogus, dus letterlijk
// het vinkje dat de beheerder in Instellingen → Rollen moet aanzetten.
//
// De strook zegt het vooraf; wie tóch op een uitgeschakeld veld of knop klikt,
// krijgt daarnaast een venster dat zegt dat de actie buiten zijn rol valt. Een
// uitgeschakeld veld krijgt geen `click`, ook niet borrelend naar dit blok,
// maar wél `pointerdown`/`pointerup` met zichzelf als doel (in Chrome
// nagemeten). Daarop luisteren we, zodat het veld verder blijft wat het was:
// een tekstvak scrolt nog, en de cursor en hovertekst blijven werken.

/**
 * @param {object} props
 * @param {string} props.edit - Sleutel van het bewerkrecht, bijv. 'strategy.edit'
 * @param {React.ReactNode | ((canEdit: boolean) => React.ReactNode)} props.children - De inhoud, of een functie die `canEdit` ontvangt
 */
const ReadOnlyGuard = ({ edit, children }) => {
    const { has, ready } = usePermissions()
    const canEdit = !ready || has(edit)

    // `canEdit` is waar zolang de rechten laden, dus `!canEdit` betekent
    // altijd: geladen én het recht ontbreekt. De melding kan daardoor niet
    // even opflitsen op een scherm dat de gebruiker wél mag bewerken.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const permissionName = permissionLabel(edit)
    const [deniedOpen, setDeniedOpen] = useState(false)
    /** Het veld waarop de druk begon; pas als hij daar ook eindigt is het een klik. */
    /** @type {React.MutableRefObject<Element | null>} */
    const pressedRef = useRef(null)

    /**
     * Het uitgeschakelde veld onder deze pointer-event, of null.
     *
     * Niet meegeteld: een druk op de schuifbalk van een tekstvak (wie die pakt
     * wil een lang verslag lezen, niet bewerken), en alles buiten dit blok —
     * een venster dat de inhoud via een portal opent borrelt in React wél naar
     * hier.
     *
     * @param {React.PointerEvent<HTMLFieldSetElement>} e
     * @returns {Element | null}
     */
    const pressedControl = (e) => {
        if (canEdit) return null
        const control = deniedControl(e.currentTarget, e.target)
        if (!control) return null
        return onScrollbar(control, e.nativeEvent.offsetX) ? null : control
    }

    /** @param {React.PointerEvent<HTMLFieldSetElement>} e */
    const startPress = (e) => {
        pressedRef.current = pressedControl(e)
    }

    /**
     * Opent het venster als de druk op hetzelfde uitgeschakelde veld begon en
     * eindigde — zo telt tekst selecteren die op een veld begint niet mee.
     * Bij geneste poorten handelt de binnenste af: één druk is één venster,
     * met het recht van het blok waar hij in viel.
     *
     * @param {React.PointerEvent<HTMLFieldSetElement>} e
     * @returns {void}
     */
    const explainDenied = (e) => {
        const control = pressedControl(e)
        const started = pressedRef.current
        pressedRef.current = null
        if (!control || control !== started) return
        if (e.nativeEvent.readOnlyGuardHandled) return
        e.nativeEvent.readOnlyGuardHandled = true
        setDeniedOpen(true)
    }

    return (
        <>
            <fieldset className="readonly-guard" disabled={!canEdit} onPointerDown={startPress} onPointerUp={explainDenied}>
                {!canEdit && (
                    // role="status" en niet role="alert": dit is de stand van het
                    // scherm, geen onderbreking. Let op: region en tekst ontstaan
                    // hier tegelijk, dus de meeste schermlezers kondigen bij het
                    // laden niets aan — de rol helpt pas als de strook later
                    // verschijnt. Het dragende werk doet de leesvolgorde: de tekst
                    // staat vóór de inhoud waar hij over gaat.
                    <p className="readonly-guard-notice" role="status">
                        <strong>Alleen lezen.</strong>{' '}
                        {permissionName
                            ? <>Je rol heeft het recht ‘{permissionName}’ niet.</>
                            : <>Je rol heeft geen bewerkrechten voor dit onderdeel.</>}{' '}
                        Vraag een beheerder om dit recht.
                    </p>
                )}
                {typeof children === "function" ? children(canEdit) : children}
            </fieldset>
            {/* Naast de fieldset en niet erin: een klik in dit venster mag niet
                naar explainDenied borrelen. Alleen gerenderd als hij open is:
                react-modal hangt anders per poort een lege portal-div aan de
                body, en een pagina kan tientallen poorten hebben. */}
            {deniedOpen && <Modal
                modalOpen={deniedOpen}
                setModalOpen={setDeniedOpen}
                pageHeader="Dit valt buiten je rol"
                hideConfirm
                cancelLabel="Sluiten"
                content={
                    <p>
                        {permissionName
                            ? <>Je rol heeft het recht ‘{permissionName}’ niet, dus je kunt hier alleen meekijken.</>
                            : <>Je rol heeft geen bewerkrechten voor dit onderdeel, dus je kunt hier alleen meekijken.</>}{' '}
                        Vraag een beheerder om dit recht.
                    </p>
                }
            />}
        </>
    )
}

export default ReadOnlyGuard
